import { useCallback, useEffect, useRef, useState } from "react";

const useRoomMedia = () => {
  const [localStream, setLocalStream] = useState(null);
  const [permissionState, setPermissionState] = useState("idle"); // idle | requesting | granted | denied
  const [error, setError] = useState(null);
  const requestInFlight = useRef(false);

  const startMedia = useCallback(async () => {
    if (requestInFlight.current) {
      console.info("[media] request in flight, skipping");
      return localStream;
    }
    if (!navigator?.mediaDevices?.getUserMedia) {
      const err = new Error("Camera not supported in this browser/context");
      console.error("[media] getUserMedia unavailable", err);
      setPermissionState("denied");
      setError(err);
      throw err;
    }

    requestInFlight.current = true;
    console.info("[media] requesting getUserMedia");
    setPermissionState("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      console.info("[media] camera stream acquired", {
        tracks: stream.getTracks().map((t) => ({
          kind: t.kind,
          enabled: t.enabled,
          readyState: t.readyState,
        })),
      });
      setLocalStream(stream);
      setPermissionState("granted");
      setError(null);
      return stream;
    } catch (err) {
      console.error("[media] camera permission error", err);
      setPermissionState("denied");
      setError(err);
      throw err;
    } finally {
      requestInFlight.current = false;
    }
  }, [localStream]);

  const stopMedia = useCallback(() => {
    if (localStream) {
      console.info("[media] stopping tracks");
      localStream.getTracks().forEach((track) => track.stop());
    }
    setLocalStream(null);
    setPermissionState("idle");
  }, [localStream]);

  useEffect(() => {
    return () => {
      console.info("[media] cleanup");
      // Intentionally not stopping tracks here to avoid StrictMode double-invoke
      // interfering with dev camera testing. Call stopMedia manually on leave.
    };
  }, []);

  return {
    localStream,
    startMedia,
    stopMedia,
    permissionState,
    error,
  };
};

export default useRoomMedia;

